import React, { forwardRef } from "react";
import { Send, Paperclip } from "@semo/icons";
import type { ChatInputProps } from "../types";
import { cn, Button, Textarea } from "@semo/shared-ui";
import { CHAT_ATTACHMENT_ACCEPT } from "../../utils/chat-attachment-policy";

/**
 * ChatInput - 채팅 메시지 입력 컴포넌트
 *
 * 텍스트 입력 + 파일 첨부 + 전송 버튼
 */
const ChatInputComponent = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      value,
      onChange,
      onSend,
      onFileClick,
      fileInputRef,
      onFileChange,
      onUpload,
      disabled = false,
      maxLength,
    },
    ref,
  ) => {
    /**
     * 글자 수 한도를 넘겼는가.
     *
     * 입력란에서 잘라 내지 않는다 — 사용자매뉴얼 R06 5.2.4 12번은 «1,000자를 넘겨 전송을 시도한
     * 경우» 에 «메시지는 최대 1,000자까지 전송 가능합니다.» 를 띄우도록 정한다. 잘라 내면 그 안내가
     * 뜰 수 없고, 붙여 넣은 글 뒤쪽이 말없이 사라진다. 넘긴 동안은 글자 수가 빨갛게 바뀌고,
     * 보내려 하면 부모(`ChatWindow.handleSend`)가 막고 알린다.
     */
    const isOverLimit = maxLength !== undefined && value.length > maxLength;

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Prevent sending while composing (Korean/Japanese/Chinese IME)
      if (e.nativeEvent.isComposing) return;

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSend(e as unknown as React.FormEvent);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData.items;
      for (const item of Array.from(items)) {
        if (item.type.includes("image")) {
          const file = item.getAsFile();
          if (file) {
            onUpload?.(file);
          }
        }
      }
    };

    return (
      <div
        data-slot="chat-input"
        className="p-3 sm:p-5 bg-white border-t border-slate-100 relative"
      >
        <form
          onSubmit={onSend}
          className="max-w-3xl mx-auto relative flex items-end"
        >
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            className="hidden"
            accept={CHAT_ATTACHMENT_ACCEPT}
          />

          {/* File attach button */}
          <div className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onFileClick}
              className="text-slate-400 hover:text-slate-600 rounded-full"
              aria-label="파일 첨부"
            >
              <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>

          {/* Text input area with character counter */}
          <Textarea
            ref={ref}
            value={value}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="메시지를 입력하세요..."
            disabled={disabled}
            rows={1}
            autoFocus
            aria-invalid={isOverLimit || undefined}
            className={cn(
              "w-full bg-slate-50",
              "border border-slate-200",
              // Right padding reserves room for the counter + send button
              "rounded-2xl pl-10 sm:pl-12 pr-24 sm:pr-28",
              // py(12) + leading(20) + py(12) + border(2) = 46px exactly
              "py-3 leading-5",
              "text-sm sm:text-base text-slate-900",
              "placeholder:text-slate-400",
              "focus:outline-none focus:border-[#2563eb]",
              "focus:bg-white",
              "focus:ring-4 focus:ring-primary/5",
              "transition-colors resize-none",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              // Fixed height: the input never grows, long text scrolls inside
              "field-sizing-fixed h-[46px] min-h-[46px] max-h-[46px]",
              "overflow-y-auto custom-scrollbar",
              isOverLimit &&
                "border-red-300 focus:border-red-400 focus:ring-red-500/5",
            )}
            style={{ scrollbarGutter: "stable" }} // Directly apply for guaranteed effect
            enterKeyHint="send"
          />

          {/* Character counter — overlay only, never affects input height */}
          {maxLength !== undefined && value.length > 0 && (
            <div
              className={cn(
                "absolute right-11 sm:right-14 top-1/2 -translate-y-1/2 z-10",
                "text-2xs font-medium tracking-tight tabular-nums pointer-events-none",
                isOverLimit ? "text-red-500 font-semibold" : "text-slate-400",
              )}
            >
              {value.length.toLocaleString("ko-KR")} /{" "}
              {maxLength.toLocaleString("ko-KR")}
            </div>
          )}

          {/* Send button */}
          <div className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-10">
            <Button
              type="submit"
              variant="default"
              size="icon"
              disabled={!value.trim() || disabled}
              className="rounded-full shadow-sm shadow-primary/20 w-8 h-8 sm:w-9 sm:h-9"
              aria-label="메시지 전송"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </form>
      </div>
    );
  },
);

ChatInputComponent.displayName = "ChatInput";

/**
 * ✅ React.memo wrapper to prevent unnecessary re-renders
 *
 * All props should be stable from parent's useCallback/useMemo
 */
export const ChatInput = React.memo(ChatInputComponent);
